import React, { useState, useEffect, useCallback } from 'react';
import { Crosshair, Plus, Trash, Trophy, X, FilePdf, Sparkle, PencilSimple, Printer } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { targetAPI } from '../../utils/api';
import api from '../../utils/api';

export const TargetPanel = ({ show, isAdmin }) => {
  const [targets, setTargets] = useState([]);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [results, setResults] = useState(null);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  // v234 — Edit mode. `editingTarget` is the target being edited, or null.
  // When set, we render TargetFormModal in edit mode (patch instead of post).
  const [editingTarget, setEditingTarget] = useState(null);

  const loadTargets = useCallback(async () => {
    try {
      const { data } = await targetAPI.list(show.id);
      setTargets(data);
    } catch { /* silent */ }
  }, [show.id]);

  useEffect(() => { loadTargets(); }, [loadTargets]);

  const handleSelectTarget = async (tgt) => {
    setSelectedTarget(tgt);
    setResultsLoading(true);
    try {
      const { data } = await targetAPI.results(show.id, tgt.id);
      setResults(data);
    } catch {
      toast.error('Failed to load target results');
    } finally {
      setResultsLoading(false);
    }
  };

  const handleDelete = async (tgtId) => {
    try {
      await targetAPI.delete(show.id, tgtId);
      toast.success('Target deleted');
      if (selectedTarget?.id === tgtId) { setSelectedTarget(null); setResults(null); }
      loadTargets();
    } catch {
      toast.error('Failed to delete target');
    }
  };

  const classMap = {};
  (show.classes || []).forEach(c => { classMap[c.id] = c; });

  return (
    <div className="mt-8" data-testid="targets-section">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Crosshair size={24} weight="duotone" className="text-[#FF375F]" />
          <h2 className="text-xl sm:text-2xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>Target Race</h2>
        </div>
        {isAdmin && (
          <button
            onClick={() => setShowCreate(true)}
            data-testid="create-target-btn"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#FF375F]/10 border border-[#FF375F]/30 text-[#FF375F] font-semibold rounded-md hover:bg-[#FF375F]/20 transition-colors text-sm"
          >
            <Plus size={14} weight="bold" /> New Target
          </button>
        )}
      </div>

      {targets.length === 0 && !showCreate ? (
        <div className="bg-[#161616] border border-[#262626] rounded-lg p-8 text-center">
          <Crosshair size={40} className="text-[#262626] mx-auto mb-3" />
          <p className="text-[#9E9E9E] text-sm">No target races configured for this show</p>
          {isAdmin && <p className="text-[#555] text-xs mt-1">Click "New Target" — closest 2 times across days wins</p>}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {targets.map((tgt) => (
            <div
              key={tgt.id}
              data-testid={`target-card-${tgt.id}`}
              className={`bg-[#161616] border rounded-lg p-5 cursor-pointer transition-all hover:-translate-y-0.5 ${
                selectedTarget?.id === tgt.id ? 'border-[#FF375F]' : 'border-[#262626] hover:border-[#FF375F]/50'
              }`}
              onClick={() => handleSelectTarget(tgt)}
            >
              <div className="flex items-start justify-between mb-3">
                <h4 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit' }}>{tgt.name}</h4>
                {isAdmin && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={(e) => { e.stopPropagation(); setEditingTarget(tgt); }}
                      data-testid={`edit-target-${tgt.id}`}
                      title="Edit target race"
                      className="text-[#9E9E9E]/60 hover:text-[#FF375F] transition-colors p-1"
                    >
                      <PencilSimple size={14} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(tgt.id); }}
                      data-testid={`delete-target-${tgt.id}`}
                      className="text-[#FF3B30]/60 hover:text-[#FF3B30] transition-colors p-1"
                    >
                      <Trash size={14} />
                    </button>
                  </div>
                )}
              </div>
              <p className="text-xs text-[#9E9E9E] mb-2">{tgt.selected_class_ids.length} classes &middot; Closest 2 times</p>
              <div className="space-y-1">
                {tgt.selected_class_ids.map(cid => (
                  <p key={cid} className="text-xs text-[#FF375F]/70">{classMap[cid]?.name || cid}</p>
                ))}
              </div>
              <div className="mt-3 pt-3 border-t border-[#262626] flex justify-between items-center text-xs">
                <div className="flex flex-col gap-0.5">
                  <span className="text-[#9E9E9E]">{tgt.payout_type === 'PRIZES_ONLY' ? 'Prizes Only' : 'From Entries'}</span>
                  {Number(tgt.entry_fee) > 0 && (
                    <span className="text-[#34C759] font-bold" data-testid={`target-card-fee-${tgt.id}`}>
                      ${Number(tgt.entry_fee).toFixed(0)} entry fee
                    </span>
                  )}
                </div>
                <span className="text-white font-semibold">{tgt.places_paid} places</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Results Display */}
      {selectedTarget && (
        <div className="bg-[#161616] border border-[#262626] rounded-lg overflow-hidden" data-testid="target-results">
          <div className="px-5 py-4 border-b border-[#262626] flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit' }}>{selectedTarget.name} — Results</h3>
              <p className="text-xs text-[#555] mt-0.5">Smallest difference between closest 2 times wins</p>
              {results && (
                <p className="text-xs text-[#9E9E9E] mt-1">
                  {results.total_eligible} eligible rider{results.total_eligible !== 1 ? 's' : ''}
                  {Number(results.entry_fee) > 0 && results.opted_in_count !== null && results.opted_in_count !== undefined && (
                    <span> &middot; <span className="text-[#34C759]">{results.opted_in_count} opted in @ ${Number(results.entry_fee).toFixed(0)}</span></span>
                  )}
                  {/* v257 — PRIZES_ONLY targets never show pool $ even if added_money > 0 */}
                  {results.total_pool > 0 && results.payout_type !== 'PRIZES_ONLY' && <span> &middot; Pool: <span className="text-[#34C759]">${results.total_pool.toFixed(0)}</span></span>}
                  {results.payout_type === 'PRIZES_ONLY' && <span> &middot; <span className="text-[#AF52DE]">Prizes only</span></span>}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {/* v256 — Print / PDF the target results */}
              {(() => {
                const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
                const pdfUrl = `${BACKEND}/api/shows/${show.id}/targets/${selectedTarget.id}/results.pdf`;
                const canPrint = results && Array.isArray(results.results) && results.results.length > 0;
                return (
                  <>
                    <button
                      type="button"
                      onClick={() => window.open(`${pdfUrl}?inline=1`, '_blank', 'noopener')}
                      disabled={!canPrint}
                      data-testid="print-target-results"
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-[#0096FF]/10 border border-[#0096FF]/40 hover:bg-[#0096FF]/20 text-[#0096FF] rounded-md text-xs font-bold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      title="Open a printable PDF in a new tab"
                    ><Printer size={13} weight="bold" /> Print</button>
                    <button
                      type="button"
                      onClick={() => window.open(pdfUrl, '_blank', 'noopener')}
                      disabled={!canPrint}
                      data-testid="download-target-results-pdf"
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-[#161616] border border-[#333] hover:border-[#0096FF] text-white rounded-md text-xs font-bold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      title="Download the target race results as PDF"
                    ><FilePdf size={13} weight="bold" /> PDF</button>
                  </>
                );
              })()}
              <button onClick={() => { setSelectedTarget(null); setResults(null); }} className="text-[#9E9E9E] hover:text-white p-1">
                <X size={18} />
              </button>
            </div>
          </div>

          {isAdmin && Number(selectedTarget?.entry_fee) > 0 && (
            <TargetOptInsPanel show={show} target={selectedTarget} onChange={() => handleSelectTarget(selectedTarget)} />
          )}

          {resultsLoading ? (
            <div className="p-8 text-center"><div className="shimmer h-8 w-8 rounded-full mx-auto"></div></div>
          ) : results && Array.isArray(results.results) && results.results.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[#9E9E9E] text-xs border-b border-[#262626]">
                    <th className="px-4 py-3 text-left w-12">Place</th>
                    <th className="px-4 py-3 text-left">Rider</th>
                    {(results.selected_classes || []).map(cls => (
                      <th key={cls.id} className="px-4 py-3 text-right">{cls.name}</th>
                    ))}
                    <th className="px-4 py-3 text-right">Time 1</th>
                    <th className="px-4 py-3 text-right">Time 2</th>
                    <th className="px-4 py-3 text-right">Target Diff</th>
                    {results.total_pool > 0 && results.payout_type !== 'PRIZES_ONLY' && <th className="px-4 py-3 text-right">Payout</th>}
                  </tr>
                </thead>
                <tbody>
                  {results.results.map((r) => (
                    <tr
                      key={`${r.rider_id}-${r.horse_name || ''}`}
                      data-testid={`target-result-${r.place}`}
                      className={`border-b border-[#262626]/50 ${
                        r.place === 1 ? 'bg-[#FFD700]/5' : r.place === 2 ? 'bg-[#C0C0C0]/5' : r.place === 3 ? 'bg-[#CD7F32]/5' : ''
                      }`}
                    >
                      <td className="px-4 py-3">
                        <span className={`font-black text-base ${
                          r.place === 1 ? 'text-[#FFD700]' : r.place === 2 ? 'text-[#C0C0C0]' : r.place === 3 ? 'text-[#CD7F32]' : 'text-[#9E9E9E]'
                        }`}>
                          {r.tied ? `T-${r.place}` : r.place}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-white font-semibold">{r.rider_name}</p>
                        {r.horse_name && <p className="text-[#9E9E9E] text-xs">{r.horse_name}</p>}
                      </td>
                      {/* v251 — Every numeric render below is defensive:
                          malformed leaderboard row (missing target_diff,
                          times, or the times map) MUST NOT blank-screen
                          the whole panel. Fallback to em-dash. */}
                      {(results.selected_classes || []).map(cls => {
                        const t = r.times && r.times[cls.id];
                        return (
                          <td key={cls.id} className="px-4 py-3 text-right text-white font-mono text-xs">
                            {typeof t === 'number' ? t.toFixed(3) : '—'}
                          </td>
                        );
                      })}
                      <td className="px-4 py-3 text-right text-white font-mono text-xs">{typeof r.target_time_1 === 'number' ? r.target_time_1.toFixed(3) : '—'}</td>
                      <td className="px-4 py-3 text-right text-white font-mono text-xs">{typeof r.target_time_2 === 'number' ? r.target_time_2.toFixed(3) : '—'}</td>
                      <td className="px-4 py-3 text-right font-mono font-bold text-[#FF375F]">{typeof r.target_diff === 'number' ? r.target_diff.toFixed(3) : '—'}</td>
                      {results.total_pool > 0 && results.payout_type !== 'PRIZES_ONLY' && (
                        <td className="px-4 py-3 text-right text-[#34C759] font-semibold">
                          {typeof r.payout === 'number' && r.payout > 0 ? `$${r.payout.toFixed(0)}` : '—'}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : results ? (
            <div className="p-8 text-center">
              <Trophy size={32} className="text-[#262626] mx-auto mb-2" />
              <p className="text-[#9E9E9E] text-sm">No eligible riders yet</p>
              <p className="text-[#555] text-xs mt-1">Riders need completed runs in at least 2 selected classes</p>
            </div>
          ) : null}
        </div>
      )}

      {showCreate && (
        <CreateTargetModal
          show={show}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); loadTargets(); }}
        />
      )}

      {editingTarget && (
        <CreateTargetModal
          show={show}
          existingTarget={editingTarget}
          onClose={() => setEditingTarget(null)}
          onCreated={async () => {
            const wasSelected = selectedTarget?.id === editingTarget.id;
            setEditingTarget(null);
            await loadTargets();
            // If we were viewing this target's results, re-fetch since
            // categories / classes / entry_fee / places_paid may have
            // changed the payout math.
            if (wasSelected) {
              const { data } = await targetAPI.list(show.id);
              const fresh = data.find((t) => t.id === selectedTarget.id);
              if (fresh) await handleSelectTarget(fresh);
            }
          }}
        />
      )}
    </div>
  );
};


const CreateTargetModal = ({ show, onClose, onCreated, existingTarget = null }) => {
  // v234 — Same modal handles both create and edit. When `existingTarget`
  // is provided we hydrate state from it and PATCH on submit instead of POST.
  const isEdit = Boolean(existingTarget);
  const [name, setName] = useState(existingTarget?.name || '');
  // Categories: [{id, name, class_ids}]. Each is a self-contained pool —
  // the rider's two times must come from inside the SAME category for them
  // to count toward the target. Producers typically add one category per
  // class type (Open / Youth / Masters), each containing that class across
  // every show day.
  const [categories, setCategories] = useState(
    existingTarget?.categories && existingTarget.categories.length > 0
      ? existingTarget.categories.map((c) => ({
          id: c.id || `cat_${Date.now()}`,
          name: c.name || '',
          class_ids: [...(c.class_ids || [])],
        }))
      : [{ id: `cat_${Date.now()}`, name: '', class_ids: [] }]
  );
  const [payoutType, setPayoutType] = useState(existingTarget?.payout_type || 'FROM_ENTRIES');
  const [placesPaid, setPlacesPaid] = useState(existingTarget?.places_paid ?? 4);
  const [addedMoney, setAddedMoney] = useState(existingTarget?.added_money ?? 0);
  const [paybackPercent, setPaybackPercent] = useState(existingTarget?.payback_percent ?? 70);
  const [entryFee, setEntryFee] = useState(existingTarget?.entry_fee ?? 0);
  const [saving, setSaving] = useState(false);

  const addCategory = () =>
    setCategories(prev => [...prev, { id: `cat_${Date.now()}_${prev.length}`, name: '', class_ids: [] }]);
  const removeCategory = (idx) =>
    setCategories(prev => prev.filter((_, i) => i !== idx));
  const updateCategoryName = (idx, name) =>
    setCategories(prev => prev.map((c, i) => i === idx ? { ...c, name } : c));
  const toggleCategoryClass = (idx, classId) =>
    setCategories(prev => prev.map((c, i) => {
      if (i !== idx) return c;
      const cls = c.class_ids.includes(classId)
        ? c.class_ids.filter(id => id !== classId)
        : [...c.class_ids, classId];
      return { ...c, class_ids: cls };
    }));

  const handleSubmit = async () => {
    if (!name.trim()) { toast.error('Enter a name for the target race'); return; }
    const cleaned = categories
      .map(c => ({ ...c, name: c.name.trim() }))
      .filter(c => c.name && c.class_ids.length >= 2);
    if (cleaned.length === 0) {
      toast.error('Add at least one category with a name and 2+ classes');
      return;
    }
    if (cleaned.length !== categories.length) {
      toast.error('Each category needs a name and at least 2 classes');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        categories: cleaned,
        payout_type: payoutType,
        places_paid: Number(placesPaid),
        added_money: Number(addedMoney),
        payback_percent: Number(paybackPercent),
        entry_fee: Number(entryFee),
      };
      if (isEdit) {
        await targetAPI.update(show.id, existingTarget.id, payload);
        toast.success('Target race updated!');
      } else {
        await targetAPI.create(show.id, payload);
        toast.success('Target race created!');
      }
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to ${isEdit ? 'update' : 'create'} target`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#161616] border border-[#262626] rounded-xl w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-[#262626] flex items-center justify-between sticky top-0 bg-[#161616] z-10">
          <div>
            <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>
              {isEdit ? 'Edit Target Race' : 'Create Target Race'}
            </h3>
            <p className="text-xs text-[#555] mt-0.5">Closest 2 times within a single category wins</p>
          </div>
          <button onClick={onClose} className="text-[#9E9E9E] hover:text-white"><X size={20} /></button>
        </div>

        <div className="px-6 py-5 space-y-5">
          <div>
            <label className="text-sm font-semibold text-[#9E9E9E] block mb-2">Target Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Open Barrels Target Race"
              data-testid="target-name-input"
              className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-[#262626] rounded-lg text-white text-sm placeholder:text-[#555] focus:border-[#FF375F] focus:outline-none transition-colors"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-semibold text-[#9E9E9E]">
                Categories <span className="text-[#555] font-normal">(times must come from within the same category)</span>
              </label>
              <button
                type="button"
                onClick={addCategory}
                className="inline-flex items-center gap-1 px-2 py-1 bg-[#FF375F]/10 border border-[#FF375F]/30 text-[#FF375F] text-xs font-semibold rounded-md hover:bg-[#FF375F]/20 transition-colors"
                data-testid="target-add-category-btn"
              >
                <Plus size={12} /> Add Category
              </button>
            </div>
            <p className="text-xs text-[#555] mb-3">
              Example: <span className="text-[#9E9E9E]">"Open Barrels"</span> &middot; check Open Friday + Open Saturday.
              Add another category for <span className="text-[#9E9E9E]">"Youth Barrels"</span> with the youth runs across days.
            </p>
            <div className="space-y-3">
              {categories.map((cat, ci) => (
                <div key={cat.id} className="bg-[#0A0A0A] border border-[#262626] rounded-lg p-3" data-testid={`target-category-${ci}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="text"
                      value={cat.name}
                      onChange={(e) => updateCategoryName(ci, e.target.value)}
                      placeholder="Category name (e.g. Open Barrels)"
                      className="flex-1 px-2 py-1.5 bg-[#161616] border border-[#262626] rounded text-white text-sm placeholder:text-[#555] focus:border-[#FF375F] focus:outline-none"
                      data-testid={`target-category-name-${ci}`}
                    />
                    {categories.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeCategory(ci)}
                        className="p-1.5 text-[#FF3B30]/70 hover:text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded transition-colors"
                        data-testid={`target-category-remove-${ci}`}
                        title="Remove category"
                      >
                        <Trash size={14} />
                      </button>
                    )}
                  </div>
                  <div className="space-y-1">
                    {show.classes.map(cls => {
                      const checked = cat.class_ids.includes(cls.id);
                      return (
                        <div
                          key={cls.id}
                          onClick={() => toggleCategoryClass(ci, cls.id)}
                          className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer transition-colors text-xs ${
                            checked
                              ? 'bg-[#FF375F]/10 border border-[#FF375F]/30'
                              : 'bg-[#161616] border border-transparent hover:border-[#262626]'
                          }`}
                          data-testid={`target-category-${ci}-class-${cls.id}`}
                        >
                          <div className={`w-3.5 h-3.5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                            checked ? 'bg-[#FF375F] border-[#FF375F]' : 'border-[#555]'
                          }`}>
                            {checked && <span className="text-white text-[8px] font-bold">&#10003;</span>}
                          </div>
                          <span className="flex-1 truncate text-white">{cls.name}</span>
                          <span className="text-[#9E9E9E]">${cls.entry_fee}</span>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-[10px] text-[#555] mt-1.5">{cat.class_ids.length} of {show.classes.length} selected{cat.class_ids.length < 2 ? ' — need at least 2' : ''}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm font-semibold text-[#9E9E9E] block mb-2">Payout Type</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setPayoutType('FROM_ENTRIES')}
                data-testid="target-payout-entries"
                className={`p-3 rounded-lg border text-sm font-semibold transition-all ${
                  payoutType === 'FROM_ENTRIES'
                    ? 'bg-[#FF375F]/10 border-[#FF375F] text-[#FF375F]'
                    : 'bg-[#0A0A0A] border-[#262626] text-[#9E9E9E] hover:border-[#FF375F]/50'
                }`}
              >
                From Entry Fees
              </button>
              <button
                onClick={() => setPayoutType('PRIZES_ONLY')}
                data-testid="target-payout-prizes"
                className={`p-3 rounded-lg border text-sm font-semibold transition-all ${
                  payoutType === 'PRIZES_ONLY'
                    ? 'bg-[#FF375F]/10 border-[#FF375F] text-[#FF375F]'
                    : 'bg-[#0A0A0A] border-[#262626] text-[#9E9E9E] hover:border-[#FF375F]/50'
                }`}
              >
                Prizes Only
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold text-[#9E9E9E] block mb-2">Places Paid</label>
              <select
                value={placesPaid}
                onChange={(e) => setPlacesPaid(Number(e.target.value))}
                data-testid="target-places-paid"
                className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-[#262626] rounded-lg text-white text-sm focus:border-[#FF375F] focus:outline-none"
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            {payoutType === 'FROM_ENTRIES' && (
              <div>
                <label className="text-sm font-semibold text-[#9E9E9E] block mb-2">Payback %</label>
                <input
                  type="number"
                  value={paybackPercent}
                  onChange={(e) => setPaybackPercent(e.target.value)}
                  data-testid="target-payback-pct"
                  className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-[#262626] rounded-lg text-white text-sm focus:border-[#FF375F] focus:outline-none"
                />
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold text-[#9E9E9E] block mb-2">Added Money ($)</label>
              <input
                type="number"
                value={addedMoney}
                onChange={(e) => setAddedMoney(e.target.value)}
                data-testid="target-added-money"
                className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-[#262626] rounded-lg text-white text-sm focus:border-[#FF375F] focus:outline-none"
                placeholder="0"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-[#9E9E9E] block mb-2 flex items-center gap-1">
                Entry Fee ($)
                <span className="text-[10px] text-[#555] font-normal">(rider opt-in)</span>
              </label>
              <input
                type="number"
                min="0"
                value={entryFee}
                onChange={(e) => setEntryFee(e.target.value)}
                data-testid="target-entry-fee"
                className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-[#262626] rounded-lg text-white text-sm focus:border-[#FF375F] focus:outline-none"
                placeholder="0"
              />
              <p className="text-[10px] text-[#555] mt-1">When &gt; $0, only riders who pay this fee are eligible. Adds to the pool.</p>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-[#262626] flex gap-3 justify-end sticky bottom-0 bg-[#161616]">
          <button onClick={onClose} className="px-4 py-2 rounded-md text-sm font-semibold text-[#9E9E9E] bg-[#262626] hover:bg-[#333] transition-colors">Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={saving || categories.some(c => !c.name.trim() || c.class_ids.length < 2) || !name.trim()}
            data-testid={isEdit ? 'target-update-submit' : 'target-create-submit'}
            className="px-5 py-2 rounded-md text-sm font-semibold text-white bg-[#FF375F] hover:bg-[#FF375F]/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (isEdit ? 'Saving...' : 'Creating...') : (isEdit ? 'Save Changes' : 'Create Target')}
          </button>
        </div>
      </div>
    </div>
  );
};


// Producer-only: shows the table of riders who've opted into the target race
// and lets the producer toggle paid status, remove opt-ins, and manually add
// new rider+horse participants. Times so far are shown per-class per-combo
// so the office can see who's on track before the last go.
// v230: exported so ManageEntries → Targets tab can reuse the same UI.
export const TargetOptInsPanel = ({ show, target, onChange }) => {
  const [optIns, setOptIns] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [addRider, setAddRider] = useState('');
  const [addHorse, setAddHorse] = useState('');
  const [addSelectedCats, setAddSelectedCats] = useState([]);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await targetAPI.listOptIns(show.id, target.id);
      setOptIns(data.opt_ins || []);
      setCategories(data.categories || []);
      // Default add-form cats = all categories on the target (matches rider-side default)
      if ((data.categories || []).length > 0) {
        setAddSelectedCats((prev) => prev.length ? prev : (data.categories || []).map((c) => c.id));
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [show.id, target.id]);

  useEffect(() => { load(); }, [load]);

  const togglePaid = async (oi) => {
    const next = oi.payment_status === 'PAID' ? 'PENDING' : 'PAID';
    try {
      await targetAPI.updateOptIn(show.id, target.id, oi.id, next);
      toast.success(`Marked ${oi.rider_name} ${next}`);
      load();
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const remove = async (oi) => {
    const label = `${oi.rider_name}${oi.horse_name ? ' on ' + oi.horse_name : ''}`;
    if (!window.confirm(`Remove ${label} from this target race?`)) return;
    try {
      await targetAPI.removeOptIn(show.id, target.id, oi.id);
      toast.success('Opt-in removed');
      load();
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Remove failed');
    }
  };

  const toggleAddCat = (cid) => {
    setAddSelectedCats((prev) => prev.includes(cid) ? prev.filter((x) => x !== cid) : [...prev, cid]);
  };

  const handleAdd = async () => {
    const rider = addRider.trim();
    const horse = addHorse.trim();
    if (!rider) { toast.error('Rider name required'); return; }
    if (!horse) { toast.error('Horse name required'); return; }
    if (addSelectedCats.length === 0) { toast.error('Pick at least one category'); return; }
    setAdding(true);
    try {
      // Synthetic rider_id keyed by name so producer-added participants
      // dedupe with the auto-created opt-ins that come from rider-side entry.
      const synthId = `manual:${rider.toLowerCase()}`;
      await targetAPI.optIn(show.id, target.id, {
        rider_id: synthId,
        rider_name: rider,
        horse_name: horse,
        selected_category_ids: addSelectedCats,
      });
      toast.success(`Added ${rider} on ${horse}`);
      setAddRider('');
      setAddHorse('');
      setShowAdd(false);
      load();
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Add failed');
    } finally {
      setAdding(false);
    }
  };

  const downloadRosterPdf = async (paidOnly = true) => {
    try {
      const resp = await api.get(
        `/shows/${show.id}/targets/${target.id}/roster.pdf?paid_only=${paidOnly}`,
        { responseType: 'blob' },
      );
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const safeName = (target.name || 'target').replace(/[^a-z0-9]+/gi, '_');
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${safeName}_roster.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to download roster');
    }
  };

  const catNameById = {};
  categories.forEach((c) => { catNameById[c.id] = c.name; });

  // Inline "Set Horse" — producers use this to fill in a blank horse cell
  // (legacy opt-in from before v230, or an entry the rider submitted without
  // a horse name). Keyed by opt-in id so editing one row doesn't affect the
  // others.
  const [editingHorseId, setEditingHorseId] = useState(null);
  const [horseDraft, setHorseDraft] = useState('');
  const startEditHorse = (oi) => {
    setEditingHorseId(oi.id);
    setHorseDraft(oi.horse_name || '');
  };
  const cancelEditHorse = () => {
    setEditingHorseId(null);
    setHorseDraft('');
  };
  const saveEditHorse = async (oi) => {
    const next = horseDraft.trim();
    if (next === (oi.horse_name || '')) { cancelEditHorse(); return; }
    try {
      await targetAPI.patchOptIn(show.id, target.id, oi.id, { horse_name: next });
      toast.success(next ? `Set horse to ${next}` : 'Cleared horse');
      cancelEditHorse();
      load();
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  // v233 — Backfill wizard state. Producer taps "Backfill horses" and we
  // fetch suggestions from the API. Recommendations are pre-selected; multi-
  // candidate rows let the producer pick from a dropdown; empty-candidate
  // rows can be typed manually. Skip = leave blank.
  const [showBackfill, setShowBackfill] = useState(false);
  const [bfLoading, setBfLoading] = useState(false);
  const [bfSuggestions, setBfSuggestions] = useState([]);
  const [bfPicks, setBfPicks] = useState({});
  const [bfSaving, setBfSaving] = useState(false);
  const hasBlanks = optIns.some((oi) => !((oi.horse_name || '').trim()));

  const openBackfill = async () => {
    setShowBackfill(true);
    setBfLoading(true);
    try {
      const { data } = await targetAPI.backfillSuggestions(show.id, target.id);
      const sug = data.needs_backfill || [];
      setBfSuggestions(sug);
      const initial = {};
      sug.forEach((s) => { initial[s.opt_in_id] = s.recommended || ''; });
      setBfPicks(initial);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load suggestions');
      setShowBackfill(false);
    } finally {
      setBfLoading(false);
    }
  };

  const setBfPick = (id, val) => setBfPicks((prev) => ({ ...prev, [id]: val }));

  const applyBackfill = async () => {
    const toApply = bfSuggestions
      .map((s) => ({ id: s.opt_in_id, horse: (bfPicks[s.opt_in_id] || '').trim() }))
      .filter((x) => x.horse);
    if (toApply.length === 0) { toast.error('Pick at least one horse to save'); return; }
    setBfSaving(true);
    let ok = 0;
    const errors = [];
    for (const { id, horse } of toApply) {
      try {
        await targetAPI.patchOptIn(show.id, target.id, id, { horse_name: horse });
        ok += 1;
      } catch (e) {
        errors.push(`${horse}: ${e.response?.data?.detail || 'failed'}`);
      }
    }
    setBfSaving(false);
    if (ok > 0) toast.success(`Backfilled ${ok} opt-in${ok === 1 ? '' : 's'}`);
    if (errors.length > 0) toast.error(errors.slice(0, 2).join(' · '));
    setShowBackfill(false);
    load();
    onChange?.();
  };

  return (
    <div className="px-5 py-4 border-b border-[#262626] bg-[#0A0A0A]/40" data-testid={`target-opt-ins-${target.id}`}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h4 className="text-sm font-bold text-white" style={{ fontFamily: 'Outfit' }}>
            Participants ({optIns.length})
          </h4>
          <p className="text-xs text-[#9E9E9E] mt-0.5">
            {Number(target.entry_fee) > 0 ? (
              <>
                Fee: <span className="text-[#34C759] font-semibold">${(Number(target.entry_fee) || 0).toFixed(0)}</span> per rider+horse
                {' · '}Same rider on 2 horses = 2 opt-ins = 2 fees
              </>
            ) : (
              <span>Free target · rider+horse combo is the identity</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {hasBlanks && (
            <button
              onClick={openBackfill}
              data-testid={`target-backfill-btn-${target.id}`}
              title="Auto-suggest horse names for rows with blank horse cells"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#FFCC00]/10 border border-[#FFCC00]/40 text-[#FFCC00] text-xs font-semibold rounded-md hover:bg-[#FFCC00]/20 transition-colors"
            >
              <Sparkle size={12} weight="bold" /> Backfill horses
            </button>
          )}
          <button
            onClick={() => downloadRosterPdf(true)}
            data-testid={`target-roster-pdf-btn-${target.id}`}
            title="Download printable roster (paid participants)"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#0A0A0A] border border-[#262626] text-[#9E9E9E] text-xs font-semibold rounded-md hover:text-white hover:border-[#FF375F]/50 transition-colors"
          >
            <FilePdf size={12} weight="bold" /> Roster PDF
          </button>
          <button
            onClick={() => setShowAdd((v) => !v)}
            data-testid={`target-add-participant-btn-${target.id}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#FF375F]/10 border border-[#FF375F]/30 text-[#FF375F] text-xs font-semibold rounded-md hover:bg-[#FF375F]/20 transition-colors"
          >
            <Plus size={12} weight="bold" /> {showAdd ? 'Close' : 'Add Participant'}
          </button>
        </div>
      </div>

      {showAdd && (
        <div className="mb-3 p-3 bg-[#0A0A0A] border border-[#FF375F]/30 rounded-lg space-y-2" data-testid={`target-add-form-${target.id}`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input
              value={addRider}
              onChange={(e) => setAddRider(e.target.value)}
              placeholder="Rider name"
              data-testid={`target-add-rider-${target.id}`}
              className="px-3 py-2 bg-[#161616] border border-[#262626] rounded text-white text-sm placeholder:text-[#555] focus:border-[#FF375F] focus:outline-none"
            />
            <input
              value={addHorse}
              onChange={(e) => setAddHorse(e.target.value)}
              placeholder="Horse name"
              data-testid={`target-add-horse-${target.id}`}
              className="px-3 py-2 bg-[#161616] border border-[#262626] rounded text-white text-sm placeholder:text-[#555] focus:border-[#FF375F] focus:outline-none"
            />
          </div>
          {categories.length > 1 && (
            <div>
              <p className="text-[10px] text-[#9E9E9E] mb-1">Categories opted into</p>
              <div className="flex flex-wrap gap-1.5">
                {categories.map((c) => {
                  const on = addSelectedCats.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => toggleAddCat(c.id)}
                      data-testid={`target-add-cat-${target.id}-${c.id}`}
                      className={`px-2 py-1 rounded text-[11px] font-semibold border transition-colors ${
                        on
                          ? 'bg-[#FF375F]/20 border-[#FF375F] text-[#FF375F]'
                          : 'bg-[#161616] border-[#262626] text-[#9E9E9E] hover:border-[#FF375F]/40'
                      }`}
                    >
                      {c.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <button
              onClick={() => { setShowAdd(false); setAddRider(''); setAddHorse(''); }}
              className="px-3 py-1.5 rounded text-xs font-semibold text-[#9E9E9E] bg-[#262626] hover:bg-[#333]"
            >
              Cancel
            </button>
            <button
              onClick={handleAdd}
              disabled={adding}
              data-testid={`target-add-submit-${target.id}`}
              className="px-3 py-1.5 rounded text-xs font-semibold text-white bg-[#FF375F] hover:bg-[#FF375F]/80 disabled:opacity-50"
            >
              {adding ? 'Adding…' : 'Add'}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-xs text-[#555]">Loading…</p>
      ) : optIns.length === 0 ? (
        <p className="text-xs text-[#9E9E9E] py-2">No participants yet. Riders opt in via the entry form, or use &ldquo;Add Participant&rdquo;.</p>
      ) : (
        <div className="overflow-x-auto -mx-1">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[#9E9E9E] border-b border-[#262626]">
                <th className="text-left px-2 py-1.5 font-semibold">Rider</th>
                <th className="text-left px-2 py-1.5 font-semibold">Horse</th>
                <th className="text-left px-2 py-1.5 font-semibold">Categories</th>
                <th className="text-left px-2 py-1.5 font-semibold">Runs</th>
                <th className="text-center px-2 py-1.5 font-semibold w-24">Payment</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {optIns.map((oi) => {
                const optCats = (oi.selected_category_ids || []).map((cid) => catNameById[cid]).filter(Boolean);
                const completed = (oi.runs || []).filter((r) => r.status === 'COMPLETED' && r.time != null);
                return (
                  <tr key={oi.id} className="border-b border-[#262626]/50" data-testid={`target-opt-in-row-${oi.id}`}>
                    <td className="px-2 py-2 text-white font-semibold">{oi.rider_name || oi.rider_id}</td>
                    <td className="px-2 py-2 text-white">
                      {editingHorseId === oi.id ? (
                        <div className="flex items-center gap-1">
                          <input
                            autoFocus
                            value={horseDraft}
                            onChange={(e) => setHorseDraft(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') saveEditHorse(oi);
                              if (e.key === 'Escape') cancelEditHorse();
                            }}
                            placeholder="Horse name"
                            data-testid={`target-opt-in-horse-input-${oi.id}`}
                            className="px-1.5 py-0.5 bg-[#0A0A0A] border border-[#FF375F] rounded text-white text-xs w-32 focus:outline-none"
                          />
                          <button
                            onClick={() => saveEditHorse(oi)}
                            data-testid={`target-opt-in-horse-save-${oi.id}`}
                            className="text-[10px] font-bold text-[#34C759] hover:text-white px-1"
                          >
                            ✓
                          </button>
                          <button
                            onClick={cancelEditHorse}
                            className="text-[10px] font-bold text-[#9E9E9E] hover:text-white px-1"
                          >
                            ✕
                          </button>
                        </div>
                      ) : oi.horse_name ? (
                        <button
                          type="button"
                          onClick={() => startEditHorse(oi)}
                          data-testid={`target-opt-in-horse-${oi.id}`}
                          title="Click to edit"
                          className="text-left hover:text-[#FF375F] transition-colors"
                        >
                          {oi.horse_name}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => startEditHorse(oi)}
                          data-testid={`target-opt-in-set-horse-${oi.id}`}
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[#FF375F]/10 border border-[#FF375F]/40 text-[#FF375F] text-[10px] font-bold hover:bg-[#FF375F]/20 transition-colors"
                        >
                          <Plus size={9} weight="bold" /> Set horse
                        </button>
                      )}
                    </td>
                    <td className="px-2 py-2">
                      {optCats.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {optCats.map((n, i) => (
                            <span key={i} className="px-1.5 py-0.5 rounded bg-[#FF375F]/10 text-[#FF375F] text-[10px] font-semibold">{n}</span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-[#555] text-[10px]">All</span>
                      )}
                    </td>
                    <td className="px-2 py-2">
                      <div className="flex items-center gap-1.5">
                        <span
                          className={`text-[11px] font-bold ${oi.eligible ? 'text-[#34C759]' : 'text-[#9E9E9E]'}`}
                          title="Completed runs (need 2+ in the same category to be eligible)"
                        >
                          {completed.length}
                          <span className="text-[#555] font-normal"> / {(oi.runs || []).length}</span>
                        </span>
                        {oi.eligible && (
                          <span className="text-[8px] uppercase tracking-wider bg-[#34C759]/15 text-[#34C759] px-1 py-0.5 rounded font-bold">Eligible</span>
                        )}
                      </div>
                      {/* v249 — Show the smallest per-category diff between
                          two completed times so producers can see each
                          rider's target-race score at a glance.
                          v249-hotfix: defensive null-safety on every field
                          so a partial payload can never blank-screen the
                          panel. */}
                      {oi.target_diff && typeof oi.target_diff.diff === 'number' && (
                        <div
                          className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[#0096FF]/10 border border-[#0096FF]/30 text-[10px] font-bold text-[#0096FF]"
                          data-testid={`target-diff-${oi.id}`}
                          title={
                            (typeof oi.target_diff.time_1 === 'number' && typeof oi.target_diff.time_2 === 'number')
                              ? `Best diff in ${oi.target_diff.category_name || ''}: ${oi.target_diff.time_1.toFixed(3)} vs ${oi.target_diff.time_2.toFixed(3)}`
                              : `Best diff in ${oi.target_diff.category_name || ''}`
                          }
                        >
                          Diff: {oi.target_diff.diff.toFixed(3)}s
                          {oi.target_diff.category_name && (
                            <span className="text-[9px] text-[#0096FF]/70 font-normal">· {oi.target_diff.category_name}</span>
                          )}
                        </div>
                      )}
                      {completed.length > 0 && (
                        <div className="text-[10px] text-[#9E9E9E] mt-0.5 font-mono">
                          {completed.slice(0, 3).map((r, i) => (
                            <span key={i}>{r.time.toFixed(3)}{i < Math.min(completed.length, 3) - 1 ? ', ' : ''}</span>
                          ))}
                          {completed.length > 3 && <span> +{completed.length - 3}</span>}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-2 text-center">
                      <button
                        onClick={() => togglePaid(oi)}
                        data-testid={`target-opt-in-toggle-${oi.id}`}
                        className={`px-2.5 py-1 rounded text-[10px] font-bold uppercase tracking-wide ${
                          oi.payment_status === 'PAID'
                            ? 'bg-[#34C759]/15 text-[#34C759] hover:bg-[#34C759]/25'
                            : 'bg-[#FF9500]/15 text-[#FF9500] hover:bg-[#FF9500]/25'
                        }`}
                      >
                        {oi.payment_status}
                      </button>
                    </td>
                    <td className="px-2 py-2 text-right">
                      <button
                        onClick={() => remove(oi)}
                        data-testid={`target-opt-in-remove-${oi.id}`}
                        className="text-[#FF3B30]/60 hover:text-[#FF3B30] transition-colors p-1"
                      >
                        <Trash size={12} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showBackfill && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !bfSaving && setShowBackfill(false)}
          data-testid={`target-backfill-modal-${target.id}`}
        >
          <div
            className="bg-[#161616] border border-[#262626] rounded-xl w-full max-w-2xl max-h-[85vh] flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#262626]">
              <div>
                <h3 className="text-lg font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                  <Sparkle size={18} weight="fill" className="text-[#FFCC00]" />
                  Backfill Missing Horses
                </h3>
                <p className="text-xs text-[#9E9E9E] mt-1">
                  We looked at each rider&rsquo;s entries in this target&rsquo;s classes. Pick a horse for each blank row, then save.
                </p>
              </div>
              <button
                onClick={() => !bfSaving && setShowBackfill(false)}
                className="text-[#9E9E9E] hover:text-white p-1"
                data-testid={`target-backfill-close-${target.id}`}
              >
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {bfLoading ? (
                <p className="text-sm text-[#9E9E9E] text-center py-8">Scanning entries&hellip;</p>
              ) : bfSuggestions.length === 0 ? (
                <div className="text-center py-8">
                  <Sparkle size={32} className="text-[#262626] mx-auto mb-2" />
                  <p className="text-sm text-[#9E9E9E]">No blank horses to backfill. All good!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {bfSuggestions.map((s) => {
                    const pick = bfPicks[s.opt_in_id] || '';
                    const hasCandidates = s.candidates.length > 0;
                    return (
                      <div
                        key={s.opt_in_id}
                        className="bg-[#0A0A0A] border border-[#262626] rounded-lg p-3"
                        data-testid={`target-backfill-row-${s.opt_in_id}`}
                      >
                        <div className="flex items-baseline justify-between gap-2 mb-2">
                          <div className="text-sm font-bold text-white">{s.rider_name || s.rider_id || '—'}</div>
                          {s.recommended && (
                            <span className="text-[10px] uppercase tracking-wider bg-[#34C759]/15 text-[#34C759] px-1.5 py-0.5 rounded font-bold">
                              Recommended
                            </span>
                          )}
                        </div>
                        {s.already_used.length > 0 && (
                          <p className="text-[10px] text-[#9E9E9E] mb-2">
                            Already opted in on:{' '}
                            {s.already_used.map((h, i) => (
                              <span key={i} className="text-[#FF9500] font-semibold">{h}{i < s.already_used.length - 1 ? ', ' : ''}</span>
                            ))}
                          </p>
                        )}
                        {hasCandidates ? (
                          <div className="flex flex-wrap gap-1.5">
                            {s.candidates.map((h) => {
                              const isClaimed = s.already_used.map((x) => x.toLowerCase()).includes(h.toLowerCase());
                              const selected = pick.toLowerCase() === h.toLowerCase();
                              return (
                                <button
                                  key={h}
                                  disabled={isClaimed}
                                  onClick={() => setBfPick(s.opt_in_id, h)}
                                  data-testid={`target-backfill-pick-${s.opt_in_id}-${h}`}
                                  className={`px-2.5 py-1.5 rounded text-xs font-semibold border transition-colors ${
                                    isClaimed
                                      ? 'bg-[#161616] border-[#262626] text-[#555] cursor-not-allowed line-through'
                                      : selected
                                      ? 'bg-[#FFCC00]/20 border-[#FFCC00] text-[#FFCC00]'
                                      : 'bg-[#161616] border-[#262626] text-white hover:border-[#FFCC00]/60'
                                  }`}
                                >
                                  {h}
                                </button>
                              );
                            })}
                            <button
                              onClick={() => setBfPick(s.opt_in_id, '')}
                              data-testid={`target-backfill-skip-${s.opt_in_id}`}
                              className={`px-2.5 py-1.5 rounded text-xs font-semibold border transition-colors ${
                                !pick
                                  ? 'bg-[#262626] border-[#333] text-[#9E9E9E]'
                                  : 'bg-transparent border-[#262626] text-[#555] hover:text-[#9E9E9E]'
                              }`}
                            >
                              Skip
                            </button>
                          </div>
                        ) : (
                          <div>
                            <p className="text-[11px] text-[#9E9E9E] mb-1">No matching entries found &mdash; type manually:</p>
                            <input
                              value={pick}
                              onChange={(e) => setBfPick(s.opt_in_id, e.target.value)}
                              placeholder="Horse name (leave blank to skip)"
                              data-testid={`target-backfill-manual-${s.opt_in_id}`}
                              className="w-full px-3 py-1.5 bg-[#161616] border border-[#262626] rounded text-white text-xs placeholder:text-[#555] focus:border-[#FFCC00] focus:outline-none"
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between gap-2 px-5 py-4 border-t border-[#262626]">
              <p className="text-xs text-[#9E9E9E]">
                {(() => {
                  const willApply = bfSuggestions.filter((s) => (bfPicks[s.opt_in_id] || '').trim()).length;
                  return `${willApply} of ${bfSuggestions.length} ready to apply`;
                })()}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowBackfill(false)}
                  disabled={bfSaving}
                  className="px-4 py-2 rounded text-sm font-semibold text-[#9E9E9E] bg-[#262626] hover:bg-[#333] disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={applyBackfill}
                  disabled={bfSaving || bfLoading || bfSuggestions.length === 0}
                  data-testid={`target-backfill-apply-${target.id}`}
                  className="px-4 py-2 rounded text-sm font-semibold text-black bg-[#FFCC00] hover:bg-[#E5B800] disabled:opacity-50"
                >
                  {bfSaving ? 'Saving\u2026' : 'Apply Backfill'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
